import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ShareNetwork, ShieldCheck, Truck, ArrowsClockwise, Lock, Star, Minus, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { addToCart, toggleWishlist, isWishlisted, format, recentlyViewed, trackView, setCartOpen } = useShop();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [zoomPos, setZoomPos] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    api.get(`/products/${slug}`).then(r => { setProduct(r.data); trackView(r.data); });
    api.get(`/products/${slug}/related`).then(r => setRelated(r.data || []));
    // eslint-disable-next-line
  }, [slug]);

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[var(--bm-muted)]">Loading…</div>;
  const discount = product.compare_at_price ? Math.round(100 - (product.price / product.compare_at_price) * 100) : 0;
  const liked = isWishlisted(product.id);
  const lowStock = product.stock > 0 && product.stock <= 5;

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoomPos({ x, y });
  };
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: product.name, url }); } catch { /* ignore */ } }
    else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
  };

  return (
    <section className="py-10 lg:py-16" data-testid="product-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="grid grid-cols-12 gap-3">
            <div className="hidden md:flex md:col-span-2 flex-col gap-3 max-h-[600px] overflow-y-auto scrollbar-thin">
              {product.images?.map((img, i) => (
                <button key={i} onClick={()=>setActiveImg(i)} className={`rounded-xl overflow-hidden aspect-square ${activeImg===i?"ring-2 ring-black":"opacity-70"}`} data-testid={`thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
            <div className="col-span-12 md:col-span-10">
              <div
                className="relative aspect-[4/5] bg-[#f4f4f4] rounded-2xl overflow-hidden cursor-zoom-in"
                onMouseMove={onMouseMove}
                onMouseLeave={()=>setZoomPos(null)}
                data-testid="main-image"
              >
                <img
                  src={product.images?.[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300"
                  style={zoomPos ? { transform: "scale(2.2)", transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` } : {}}
                />
                {discount > 0 && <span className="absolute top-4 left-4 bg-white text-xs tracking-[0.2em] uppercase px-3 py-1 rounded-full border border-gray-200" data-testid="product-discount-badge">-{discount}%</span>}
              </div>
              <div className="md:hidden flex gap-2 mt-3 overflow-x-auto">
                {product.images?.map((img, i)=> (
                  <button key={i} onClick={()=>setActiveImg(i)} className="w-16 h-20 rounded-lg overflow-hidden shrink-0">
                    <img src={img} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Buy box */}
        <div className="lg:col-span-5">
          <div className="eyebrow">{product.category}</div>
          <h1 className="font-serif-bm text-3xl md:text-4xl mt-2" data-testid="product-name">{product.name}</h1>
          <div className="flex items-center gap-2 mt-2 text-xs text-[var(--bm-muted)]">
            <div className="flex gap-0.5">{[0,1,2,3,4].map(i=> <Star key={i} size={12} weight="fill" className="text-[var(--bm-gold)]"/>)}</div>
            <span>{product.rating} · {product.reviews_count} reviews</span>
          </div>
          <div className="mt-5 flex items-baseline gap-3" data-testid="product-price">
            <span className="font-serif-bm text-3xl">{format(product.price)}</span>
            {product.compare_at_price && <span className="text-base text-[var(--bm-muted)] line-through">{format(product.compare_at_price)}</span>}
            {discount > 0 && <span className="text-xs bg-[var(--bm-gold)]/20 text-[#7a6420] px-2 py-1 rounded-full font-medium">Save {discount}%</span>}
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs">
            {product.stock <= 0 ? (
              <span className="text-red-600 font-medium" data-testid="stock-status">● {t("product.out")}</span>
            ) : lowStock ? (
              <span className="text-[var(--bm-gold)] font-medium" data-testid="stock-status">● {t("product.low_stock", { n: product.stock })}</span>
            ) : (
              <span className="text-green-700 font-medium" data-testid="stock-status">● {t("product.in_stock")}</span>
            )}
          </div>

          <p className="text-sm text-[var(--bm-ink-2)] mt-6 leading-relaxed" data-testid="product-description">{product.description}</p>

          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border border-[var(--bm-border)] rounded-full">
              <button onClick={()=>setQty(q=>Math.max(1,q-1))} className="w-10 h-11 flex items-center justify-center" data-testid="pd-qty-dec"><Minus size={14} weight="thin"/></button>
              <span className="min-w-8 text-center text-sm" data-testid="pd-qty">{qty}</span>
              <button onClick={()=>setQty(q=>q+1)} className="w-10 h-11 flex items-center justify-center" data-testid="pd-qty-inc"><Plus size={14} weight="thin"/></button>
            </div>
            <button onClick={()=>{ addToCart(product, qty); toast.success("Added to cart"); }} className="btn-primary flex-1 justify-center" disabled={product.stock<=0} data-testid="add-to-cart-btn">{t("product.add")}</button>
          </div>
          <button onClick={()=>{ addToCart(product, qty); setCartOpen(false); window.location.href = "/checkout"; }} className="btn-accent w-full justify-center mt-3" disabled={product.stock<=0} data-testid="buy-now-btn">{t("product.buy")}</button>

          <div className="flex items-center justify-between mt-4 text-xs">
            <button onClick={()=>toggleWishlist(product)} className="flex items-center gap-1.5" data-testid="wishlist-btn">
              <Heart size={16} weight={liked?"fill":"thin"} className={liked?"text-red-500":""}/>
              {t("product.wishlist")}
            </button>
            <button onClick={share} className="flex items-center gap-1.5" data-testid="share-btn">
              <ShareNetwork size={16} weight="thin"/> {t("product.share")}
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-2 text-[10px] tracking-widest uppercase text-[var(--bm-muted)] border-t border-[var(--bm-border)] pt-5">
            <div className="flex items-center gap-1.5"><Lock size={14} weight="thin"/> SSL</div>
            <div className="flex items-center gap-1.5"><Truck size={14} weight="thin"/> Fast EU</div>
            <div className="flex items-center gap-1.5"><ArrowsClockwise size={14} weight="thin"/> 30-day</div>
          </div>

          {/* Accordion details */}
          <div className="mt-8 border-t border-[var(--bm-border)]">
            {[
              { title: t("product.features"), content: <ul className="space-y-1 list-disc list-inside text-sm text-[var(--bm-ink-2)]">{product.features?.map((f,i)=><li key={i}>{f}</li>)}</ul> },
              { title: t("product.specs"), content: <div className="grid grid-cols-2 gap-2 text-sm">{Object.entries(product.specifications||{}).map(([k,v])=><div key={k}><div className="text-[var(--bm-muted)] text-xs">{k}</div><div>{v}</div></div>)}</div> },
              { title: t("product.shipping_info"), content: <p className="text-sm text-[var(--bm-ink-2)] leading-relaxed">Express EU delivery in 2–4 business days. Free shipping over €150. All packages are tracked and insured.</p> },
              { title: t("product.returns_info"), content: <p className="text-sm text-[var(--bm-ink-2)] leading-relaxed">30-day free returns. Items must be unworn, in original packaging.</p> },
            ].map((acc, i) => (
              <details key={i} className="border-b border-[var(--bm-border)] py-4 group" data-testid={`accordion-${i}`}>
                <summary className="flex justify-between items-center cursor-pointer text-sm tracking-wider uppercase font-medium">{acc.title}<span className="group-open:rotate-45 transition-transform">+</span></summary>
                <div className="pt-3">{acc.content}</div>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <h3 className="font-serif-bm text-2xl md:text-3xl mb-8">{t("product.related")}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="related-products">
            {related.map((p, i)=> <ProductCard key={p.id} product={p} index={i}/>)}
          </div>
        </div>
      )}
      {/* Recently viewed */}
      {recentlyViewed.length > 1 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <h3 className="font-serif-bm text-2xl md:text-3xl mb-8">{t("product.recently")}</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {recentlyViewed.filter(p=>p.id!==product.id).slice(0,4).map((p,i)=> <ProductCard key={p.id} product={p} index={i}/>)}
          </div>
        </div>
      )}
    </section>
  );
}
